import { callFinnhub } from "./finnhubClient";

const _cache = {};
const TTL    = 5 * 60 * 1000; // 5 min

// The key lives only in the Netlify function now; `load` is a call through the
// proxy. The browser-side cache is unchanged — it still saves the round trip.
// A request already on its way is shared (sprint 15, review): "מה השתנה"
// and "מה הזיז את התיק" both ask for the same symbol on first load, and the cache
// above is written only when the answer is back.
const _inflight = {};
async function _get(cacheKey, load) {
  const now = Date.now();
  const hit = _cache[cacheKey];
  if (hit && now - hit.ts < TTL) return hit.data;
  if (!_inflight[cacheKey]) {
    _inflight[cacheKey] = Promise.resolve()
      .then(load)
      .then(data => { if (data != null) _cache[cacheKey] = { data, ts: Date.now() }; return data; })
      .finally(() => { delete _inflight[cacheKey]; });
  }
  const data = await _inflight[cacheKey];
  return data ?? [];
}

// A headline that ends with its outlet's name ("… - Reuters") says the source
// twice: the card already prints it. The suffix goes when it is the article's
// own source or a known outlet, and only after a dash or a bar. A summary
// that only repeats the headline is not a summary, so it is dropped
// (2026-09-26).
const OUTLETS = ["Reuters", "Bloomberg", "CNBC", "MarketWatch", "Yahoo Finance", "Yahoo", "Barron's", "WSJ",
  "The Wall Street Journal", "Financial Times", "FT", "Associated Press", "AP", "Benzinga", "Investing.com", "Seeking Alpha", "The Motley Fool", "Motley Fool"];
const esc = t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
export function cleanHeadline(title, source) {
  const t = String(title || "").trim();
  const names = [source, ...OUTLETS].filter(Boolean).map(esc).join("|");
  return t.replace(new RegExp(`\\s+[-–—|]\\s*(?:${names})\\s*$`, "i"), "").trim() || t;
}
const norm = t => String(t || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
export function cleanExcerpt(excerpt, title) {
  const e = String(excerpt || "").trim();
  return e && norm(e) !== norm(title) ? e : "";
}

// `feed` records which endpoint the article came from. On the company feed
// Finnhub's `related` is the symbol we asked for, so it is not a tag
// (utils/newsMatch.js reads this).
function mapArticle(item, feed = "market") {
  return {
    feed,
    id:       item.id,
    title:    cleanHeadline(item.headline, item.source),
    excerpt:  cleanExcerpt(item.summary, cleanHeadline(item.headline, item.source)),
    // The Hebrew the proxy found in the shared cache (netlify/functions/lib/
    // newsHebrew.js, 26/10/2026) · null until the server has translated it.
    titleHe:  item.headline_he || null,
    excerptHe: item.summary_he || null,
    source:   item.source   || "",
    url:      item.url      || "",
    image:    item.image    || "",
    datetime: item.datetime || 0,
    symbols:  item.related
      ? String(item.related).split(",").map(s => s.trim().toUpperCase()).filter(Boolean)
      : [],
  };
}

export async function fetchMarketNews() {
  const raw = await _get("market_news", () => callFinnhub("marketNews").then(d => d?.news ?? null));
  if (!Array.isArray(raw)) return [];
  return raw.map(a => mapArticle(a, "market")).sort((a, b) => b.datetime - a.datetime);
}

// `days` sets the lookback window. It is part of the cache key, so two callers
// asking for different windows do not serve each other a truncated list.
export async function fetchCompanyNews(symbol, { days = 14 } = {}) {
  const to   = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const raw  = await _get(
    `company_news_${symbol}_${days}`,
    () => callFinnhub("companyNews", { symbol, from, to }).then(d => d?.news ?? null),
  );
  if (!Array.isArray(raw)) return [];
  return raw.map(a => mapArticle(a, "company")).sort((a, b) => b.datetime - a.datetime);
}

// `days` is passed through to each company fetch; `limit` caps the merged
// list after dedup and the newest-first sort. Finnhub returns up to ~250
// articles per symbol for two weeks, so a four-holding portfolio produced
// 1,276 cards on the news screen before either bound existed.
export async function fetchMultiCompanyNews(symbols, { days = 14, limit = Infinity } = {}) {
  if (!symbols.length) return [];
  const results = await Promise.all(symbols.slice(0, 10).map(s => fetchCompanyNews(s, { days })));
  const seen    = new Set();
  return results
    .flat()
    .filter(a => {
      const key = a.id || a.url;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => b.datetime - a.datetime)
    .slice(0, limit);
}
